import { z } from 'zod'
import { VerticalSlug } from './types'

export const ProductWarrantySchema = z.object({
  hasWarranty: z.boolean().default(false),
  warrantyType: z.enum(['tienda', 'fabricante', 'distribuidor', 'taller', 'sin_garantia']).optional(),
  durationValue: z.number().int().min(1, 'Mínimo 1').max(3650, 'Máximo 3650').optional(),
  durationUnit: z.enum(['horas', 'dias', 'meses', 'anos']).optional(),
  conditions: z.string().trim().max(300, 'Máximo 300 caracteres').optional(),
  noWarrantyReason: z.string().trim().max(200, 'Máximo 200 caracteres').optional(),
})

export type ProductWarranty = z.infer<typeof ProductWarrantySchema>

export const inmobiliariaCatalogSchema = z.object({
  propertyType: z.enum(['apartamento', 'casa', 'oficina', 'terreno', 'local', 'galpon', 'ph', 'otro']).optional(),
  operation: z.enum(['alquiler', 'venta']).optional(),
  rooms: z.number().int().min(0, 'Habitaciones no puede ser negativo').max(20, 'Máximo 20 habitaciones').optional(),
  bathrooms: z.number().int().min(0, 'Baños no puede ser negativo').max(20, 'Máximo 20 baños').optional(),
  areaM2: z.number().min(0, 'El metraje debe ser positivo').max(100000).optional(),
  parkingSpots: z.number().int().min(0, 'Estacionamientos no puede ser negativo').max(20).optional(),
  zone: z.string().trim().max(150, 'Máximo 150 caracteres').optional(),
  utilities: z.string().trim().max(150, 'Máximo 150 caracteres').optional(),
})

export const saludCatalogSchema = z.object({
  specialty: z.string().trim().max(100, 'Máximo 100 caracteres').optional(),
  practitioner: z.string().trim().max(100, 'Máximo 100 caracteres').optional(),
  durationMin: z.number().int().min(5, 'Mínimo 5 minutos').max(480, 'Máximo 480 minutos').optional(),
  modality: z.enum(['presencial', 'telemedicina']).optional(),
  prepInstructions: z.string().trim().max(200, 'Máximo 200 caracteres').optional(),
})

export const bellezaCatalogSchema = z.object({
  durationMin: z.number().int().min(5, 'Mínimo 5 minutos').max(480, 'Máximo 480 minutos').optional(),
  depositRequired: z.string().trim().max(100, 'Máximo 100 caracteres').optional(),
  specialist: z.string().trim().max(100, 'Máximo 100 caracteres').optional(),
})

export const turismoCatalogSchema = z.object({
  capacityPax: z.number().int().min(1, 'Mínimo 1 persona').max(100, 'Máximo 100 personas').optional(),
  planType: z.enum(['solo_hospedaje', 'todo_incluido']).optional(),
  boatTourIncluded: z.boolean().optional(),
  minNights: z.number().int().min(1, 'Mínimo 1 noche').max(30, 'Máximo 30 noches').optional(),
})

export const restauranteCatalogSchema = z.object({
  prepTimeMin: z.number().int().min(1, 'Mínimo 1 minuto').max(180, 'Máximo 180 minutos').optional(),
  itemType: z.enum(['plato', 'combo', 'bebida', 'extra']).optional(),
  availableToday: z.boolean().optional(),
})

export const repuestosCatalogSchema = z.object({
  brand: z.string().trim().max(80, 'Máximo 80 caracteres').optional(),
  model: z.string().trim().max(80, 'Máximo 80 caracteres').optional(),
  yearRange: z.string().trim().max(50, 'Máximo 50 caracteres').optional(),
  partType: z.enum(['oem', 'reemplazo_a', 'generico']).optional(),
})

export const bodegonCatalogSchema = z.object({
  temperature: z.enum(['frio', 'natural', 'indiferente']).optional(),
  presentation: z.string().trim().max(80, 'Máximo 80 caracteres').optional(),
  bundleComboReady: z.boolean().optional(),
})

export const ferreteriaCatalogSchema = z.object({
  unitOfMeasure: z.string().trim().max(50, 'Máximo 50 caracteres').optional(),
  gaugeOrSize: z.string().trim().max(80, 'Máximo 80 caracteres').optional(),
  dispatchType: z.enum(['moto', 'flete_pesado', 'retiro_tienda']).optional(),
})

export const serviciosCatalogSchema = z.object({
  modality: z.enum(['presencial', 'remoto', 'hibrido']).optional(),
  estimatedDuration: z.string().trim().max(100, 'Máximo 100 caracteres').optional(),
})

export const tecnologiaCatalogSchema = z.object({
  warrantyMonths: z.number().int().min(0, 'Mínimo 0 meses').max(120, 'Máximo 120 meses').optional(),
  condition: z.enum(['nuevo', 'reacondicionado', 'usado']).optional(),
})

export const educacionCatalogSchema = z.object({
  modality: z.enum(['online', 'presencial', 'hibrido']).optional(),
  duration: z.string().trim().max(100, 'Máximo 100 caracteres').optional(),
  slots: z.number().int().min(0).max(1000).optional(),
})

export const genericCatalogSchema = z.record(z.string(), z.any())

export const VERTICAL_CATALOG_SCHEMAS: Record<VerticalSlug, z.ZodType<any>> = {
  inmobiliaria: inmobiliariaCatalogSchema,
  salud: saludCatalogSchema,
  belleza_estetica: bellezaCatalogSchema,
  turismo_posadas: turismoCatalogSchema,
  restaurante: restauranteCatalogSchema,
  tienda_repuestos: repuestosCatalogSchema,
  bodegon_licoreria: bodegonCatalogSchema,
  ferreteria: ferreteriaCatalogSchema,
  servicios: serviciosCatalogSchema,
  tecnologia: tecnologiaCatalogSchema,
  educacion: educacionCatalogSchema,
  tienda: genericCatalogSchema,
  otro: genericCatalogSchema,
}

export function getVerticalCatalogSchema(slug: VerticalSlug): z.ZodType<any> {
  return VERTICAL_CATALOG_SCHEMAS[slug] || genericCatalogSchema
}
